import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { API_URL } from "../../utils/config";
import { formatDate, formatNumero } from "../../utils/Utilitats";
import { mapejos } from "../../utils/Mapejos";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import DetallsTasca from "./DetallsTasca";
import LogisticaTasca from "./LogisticaTasca";
import { ResumLogisticaTasca } from "./LogisticaTreball";
import AjudaTreball from "./AjudaTreball";
import "../dashboard_v2.css";

const VISTES = [
  { id: "General", label: "General" },
  { id: "Planificació", label: "Planificació" },
  { id: "Materials", label: "Materials" },
  { id: "Execució", label: "Execució" },
];

function classeNiuFila(level) {
  if (!level) return "";
  return `dv2-row-nest--${((Number(level) - 1) % 4) + 1}`;
}

function tascaAmbLogistica(t) {
  return Boolean(t?.Productes_idProductes || t?.Materials_idMaterials);
}

function idTascaVal(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function Tasques({ treball, onCancel, subnav, onVeureLogistica }) {
  const { rolUsuari } = useUserContext();
  const potCrear = tincPermis(rolUsuari, "CREAR");
  const potConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const potModificar = tincPermis(rolUsuari, "MODIFICAR");
  const potEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  const [tasquesList, setTasquesList] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);
  const [selectedTasca, setSelectedTasca] = useState(null);
  const [activeTab, setActiveTab] = useState("table");
  const [origenLogistica, setOrigenLogistica] = useState("table");
  const [vista, setVista] = useState("General");
  const [nouInfo, setNouInfo] = useState({
    isSubtasca: false,
    label: "Nova tasca",
    nivellText: "Nivell: 0",
    parentId: null,
  });
  const [mostraAjuda, setMostraAjuda] = useState(false);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [filtresOberts, setFiltresOberts] = useState(false);
  const menuRef = useRef(null);
  const selectedRowRef = useRef(null);

  const [fDescripcio, setFDescripcio] = useState("");
  const [fEstat, setFEstat] = useState("");
  const [fPrioritat, setFPrioritat] = useState("");
  const [fCategoria, setFCategoria] = useState("");
  const [fAgrupacio, setFAgrupacio] = useState("");
  const [fGrupMO, setFGrupMO] = useState("");

  const handleEscape = useCallback(() => {
    if (activeTab === "table") onCancel?.();
    else if (activeTab === "logistica") setActiveTab(origenLogistica);
    else setActiveTab("table");
  }, [activeTab, onCancel, origenLogistica]);
  TeclaEscEnter(handleEscape, null);

  const fetchTasques = useCallback(async () => {
    if (!treball?.idTreballs) return [];
    try {
      const resp = await fetch(`${API_URL}/TasquesTreball.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idTreballs: treball.idTreballs }),
      });
      const data = await resp.json();
      if (data.success) {
        setTasquesList(data.tasques || []);
        return data.tasques || [];
      }
      setTasquesList([]);
      return [];
    } catch (err) {
      console.error("Error carregant tasques:", err);
      setTasquesList([]);
      return [];
    }
  }, [treball]);

  useEffect(() => {
    fetchTasques();
  }, [fetchTasques]);

  useEffect(() => {
    if (activeTab === "table" && selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [activeTab, selectedTasca, tasquesList, expandedIds]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const isVisible = useCallback(
    (t) => {
      if (!t) return false;
      let parentId = idTascaVal(t.idTascaPare);
      if (parentId == null) return true;
      while (parentId != null) {
        if (!expandedIds.some((id) => idTascaVal(id) === parentId)) return false;
        const pare = tasquesList.find((x) => idTascaVal(x.idTasques) === parentId);
        parentId = idTascaVal(pare?.idTascaPare);
      }
      return true;
    },
    [expandedIds, tasquesList]
  );

  useEffect(() => {
    if (selectedTasca && !isVisible(selectedTasca)) setSelectedTasca(null);
  }, [expandedIds, selectedTasca, isVisible]);

  const teFills = (idTasca) => tasquesList.some((t) => idTascaVal(t.idTascaPare) === idTascaVal(idTasca));

  const getLevel = (t) => {
    let level = 0;
    let parentId = idTascaVal(t?.idTascaPare);
    while (parentId != null) {
      level++;
      const pare = tasquesList.find((x) => idTascaVal(x.idTasques) === parentId);
      parentId = idTascaVal(pare?.idTascaPare);
    }
    return level;
  };

  const getNewTabInfo = () => {
    if (!selectedTasca) {
      return { isSubtasca: false, label: "Nova tasca", nivellText: "Nivell: 0", parentId: null };
    }
    const isExpanded = expandedIds.includes(selectedTasca.idTasques);
    const level = getLevel(selectedTasca);
    if (isExpanded) {
      return {
        isSubtasca: true,
        label: "Nova subtasca",
        nivellText: `De: ${selectedTasca.DescripcioTasca || "—"}`,
        parentId: selectedTasca.idTasques,
      };
    }
    if (level === 0) {
      return { isSubtasca: false, label: "Nova tasca", nivellText: "Nivell: 0", parentId: null };
    }
    return {
      isSubtasca: true,
      label: "Nova subtasca",
      nivellText: `Nivell: ${level}`,
      parentId: selectedTasca.idTascaPare,
    };
  };

  const toggleExpand = (e, t) => {
    e.stopPropagation();
    setExpandedIds((prev) =>
      prev.includes(t.idTasques) ? prev.filter((id) => id !== t.idTasques) : [...prev, t.idTasques]
    );
  };

  const handleRowClick = (t) => {
    setSelectedTasca((prev) => (prev?.idTasques === t.idTasques ? null : t));
  };

  const filtrarTasques = useCallback(() => {
    const hasFilter = fDescripcio || fEstat || fPrioritat || fCategoria || fAgrupacio || fGrupMO;
    if (!hasFilter) return tasquesList;

    const matchedIds = new Set();
    for (const t of tasquesList) {
      let match = true;
      if (fDescripcio && !(t.DescripcioTasca || "").toLowerCase().includes(fDescripcio.toLowerCase())) match = false;
      if (fEstat === "__obertes" && t.Estat === "Acabat") match = false;
      else if (fEstat && fEstat !== "__obertes" && t.Estat !== fEstat) match = false;
      if (fPrioritat && t.Prioritat !== fPrioritat) match = false;
      if (fCategoria && (t.Categoria || "") !== fCategoria) match = false;
      if (fAgrupacio && String(t.AgrupacionsTreball_idAgrupacionsTreball || "") !== fAgrupacio) match = false;
      if (fGrupMO && String(t.GrupMaObra_idGrupMaObra || "") !== fGrupMO) match = false;

      if (match) {
        matchedIds.add(idTascaVal(t.idTasques));
        let ancestorId = idTascaVal(t.idTascaPare);
        while (ancestorId) {
          if (matchedIds.has(ancestorId)) break;
          matchedIds.add(ancestorId);
          const pare = tasquesList.find((x) => idTascaVal(x.idTasques) === ancestorId);
          if (!pare) break;
          ancestorId = idTascaVal(pare.idTascaPare);
        }
      }
    }
    return tasquesList.filter((t) => matchedIds.has(idTascaVal(t.idTasques)));
  }, [tasquesList, fDescripcio, fEstat, fPrioritat, fCategoria, fAgrupacio, fGrupMO]);

  const categories = [...new Set(tasquesList.map((t) => t.Categoria).filter(Boolean))];
  const agrupacions = [
    ...new Map(
      tasquesList
        .filter((t) => t.AgrupacionsTreball_idAgrupacionsTreball)
        .map((t) => [t.AgrupacionsTreball_idAgrupacionsTreball, t.NomAgrupacio])
    ).entries(),
  ];
  const grupsUnics = [
    ...new Map(
      tasquesList.filter((t) => t.GrupMaObra_idGrupMaObra).map((t) => [t.GrupMaObra_idGrupMaObra, t.NomGrupMaObra])
    ).entries(),
  ];

  const renderData = (dataReal, dataPrevista) => {
    if (dataReal) return formatDate(dataReal);
    if (dataPrevista) return <span className="dv2-date-prevista">{formatDate(dataPrevista)}</span>;
    return "—";
  };

  const renderColumnesHeader = () => {
    const accions = <th className="dv2-table-actions">Accions</th>;
    const exp = <th className="dv2-col-expand"></th>;
    switch (vista) {
      case "Planificació":
        return (
          <tr>
            {exp}
            <th>Descripció</th>
            <th>Prioritat</th>
            <th>Inici prev.</th>
            <th>Final prev.</th>
            <th>Inici</th>
            <th>Final</th>
            <th>Estat</th>
            {accions}
          </tr>
        );
      case "Materials":
        return (
          <tr>
            {exp}
            <th className="dv2-num">Quantitat</th>
            <th>Unitats</th>
            <th>Descripció</th>
            <th>Producte</th>
            <th>Material</th>
            <th>Tipus</th>
            <th>Categoria</th>
            {accions}
          </tr>
        );
      case "Execució":
        return (
          <tr>
            {exp}
            <th>Descripció</th>
            <th>Estat</th>
            <th className="dv2-num">Nº empl.</th>
            <th>Grup MO</th>
            <th className="dv2-num">Temps total</th>
            <th>Facturació</th>
            {accions}
          </tr>
        );
      default:
        return (
          <tr>
            {exp}
            <th className="dv2-num">Qtat.</th>
            <th>Unitats</th>
            <th>Descripció</th>
            <th>Grup MO</th>
            <th className="dv2-num">Durada</th>
            <th>Estat</th>
            <th>Inici</th>
            <th>Final</th>
            {accions}
          </tr>
        );
    }
  };

  const textMarcaModel = (t) => {
    if (t.Productes_idProductes) {
      const triat = `${t.MarcaProducte || ""} ${t.ModelProducte || ""}`.trim();
      return triat || t.DescripcioProducte || "—";
    }
    const suggerit = `${t.MarcaProducteSuggerit || ""} ${t.ModelProducteSuggerit || ""}`.trim();
    return suggerit ? `${suggerit} (suggerit)` : "—";
  };

  const renderColumnesRow = (t) => {
    const descProducteOMaterial = t.DescripcioProducte || t.DescripcioMaterial || "";
    const producteText = textMarcaModel(t);
    const materialText = t.NomMaterial || t.DescripcioMaterial || "—";
    const qtatReal = parseFloat(t.QuantitatReal) || 0;
    const qtatProd = parseFloat(t.QuantitatProd) || 0;
    const durada = parseFloat(t.Durada) || 0;

    switch (vista) {
      case "Planificació":
        return (
          <>
            <td className="dv2-cell-clip">{t.DescripcioTasca}{etiquetaCicle(t)}</td>
            <td>{mapejos.prioritatTasca[t.Prioritat] || t.Prioritat}</td>
            <td>{t.DataIniciPrevista ? formatDate(t.DataIniciPrevista) : "—"}</td>
            <td>{t.DataFinalPrevista ? formatDate(t.DataFinalPrevista) : "—"}</td>
            <td>{renderData(t.DataInici, null)}</td>
            <td>{renderData(t.DataFinal, null)}</td>
            <td>{mapejos.estatTasca[t.Estat] || t.Estat}</td>
          </>
        );
      case "Materials": {
        const qtatTotal = qtatReal * qtatProd;
        return (
          <>
            <td className="dv2-num">{qtatTotal > 0 ? formatNumero(qtatTotal) : "—"}</td>
            <td>{t.Unitats}</td>
            <td className="dv2-cell-clip">{descProducteOMaterial || "—"}</td>
            <td>
              {producteText}
              {Number(t.NumOfertesProducte) > 0 ? (
                <>
                  {" "}
                  <span className="dv2-chip dv2-chip--ok">Oferta</span>
                </>
              ) : null}
            </td>
            <td>{materialText}</td>
            <td>{t.TipusDeMaterial || "—"}</td>
            <td>{t.Categoria || "—"}</td>
          </>
        );
      }
      case "Execució": {
        const tempsTotal = qtatReal * durada;
        return (
          <>
            <td className="dv2-cell-clip">{t.DescripcioTasca}{etiquetaCicle(t)}</td>
            <td>{mapejos.estatTasca[t.Estat] || t.Estat}</td>
            <td className="dv2-num">{t.NumEmpleats ?? "—"}</td>
            <td>{t.NomGrupMaObra || "—"}</td>
            <td className="dv2-num">
              {tempsTotal > 0 ? `${formatNumero(tempsTotal)} ${t.UnitatsTemps || ""}` : "—"}
            </td>
            <td>{mapejos.tipusFacturacio[t.TipusFacturacio] || t.TipusFacturacio || "—"}</td>
          </>
        );
      }
      default:
        return (
          <>
            <td className="dv2-num">{t.Quantitat ?? "—"}</td>
            <td>{t.Unitats}</td>
            <td className="dv2-cell-clip">{t.DescripcioTasca}{etiquetaCicle(t)}</td>
            <td>{t.NomGrupMaObra || "—"}</td>
            <td className="dv2-num">{t.Durada ? `${t.Durada} ${t.UnitatsTemps || ""}` : "—"}</td>
            <td>{mapejos.estatTasca[t.Estat] || t.Estat}</td>
            <td>{renderData(t.DataInici, t.DataIniciPrevista)}</td>
            <td>{renderData(t.DataFinal, t.DataFinalPrevista)}</td>
          </>
        );
    }
  };

  const metaMobil = (t) => {
    if (vista === "Planificació") {
      return [mapejos.prioritatTasca[t.Prioritat] || t.Prioritat, mapejos.estatTasca[t.Estat] || t.Estat]
        .filter(Boolean)
        .join(" · ");
    }
    if (vista === "Materials") {
      const marca = textMarcaModel(t);
      return [marca !== "—" ? marca : null, t.NomMaterial || t.DescripcioMaterial, t.Categoria]
        .filter(Boolean)
        .join(" · ");
    }
    if (vista === "Execució") {
      return [mapejos.estatTasca[t.Estat] || t.Estat, t.NomGrupMaObra].filter(Boolean).join(" · ");
    }
    return [t.Quantitat && `${t.Quantitat} ${t.Unitats || ""}`, mapejos.estatTasca[t.Estat] || t.Estat]
      .filter(Boolean)
      .join(" · ");
  };

  const obrirMenu = (e, t) => {
    e.stopPropagation();
    const id = t.idTasques;
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 210;
    const alt = 250;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) top = Math.max(marge, rect.top - alt - 4);
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) left = window.innerWidth - ample - marge;
    setMenuPos({ top, left });
    setMenuObert(id);
    setSelectedTasca(t);
  };

  const renderAccions = (t) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === t.idTasques ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, t)}
      >
        …
      </button>
    </div>
  );

  const obrirConsultar = (t) => {
    setSelectedTasca(t);
    setMenuObert(null);
    setActiveTab("consultar");
  };

  const obrirLogistica = (t, origen) => {
    setSelectedTasca(t);
    setMenuObert(null);
    setOrigenLogistica(origen);
    setActiveTab("logistica");
  };

  const obrirNou = () => {
    const info = getNewTabInfo();
    setNouInfo(info);
    if (info.parentId) {
      setExpandedIds((prev) => (prev.includes(info.parentId) ? prev : [...prev, info.parentId]));
    }
    setActiveTab("nou");
  };

  const obrirNovaSubtasca = (t) => {
    setExpandedIds((prev) => (prev.includes(t.idTasques) ? prev : [...prev, t.idTasques]));
    setNouInfo({
      isSubtasca: true,
      label: "Nova subtasca",
      nivellText: `De: ${t.DescripcioTasca || "—"}`,
      parentId: t.idTasques,
    });
    setSelectedTasca(t);
    setMenuObert(null);
    setActiveTab("nou");
  };

  const compararPrevista = (a, b) => {
    const da = (a.DataIniciPrevista || "9999-12-31").toString().slice(0, 10);
    const db = (b.DataIniciPrevista || "9999-12-31").toString().slice(0, 10);
    if (da !== db) return da < db ? -1 : 1;
    return (idTascaVal(a.idTasques) || 0) - (idTascaVal(b.idTasques) || 0);
  };

  const etiquetaCicle = (t) => {
    if (t.TipusTasca !== "Ciclica") return null;
    const freq = t.FreqManual && Number(t.FreqManual) > 0 && !t.Freq
      ? `${t.FreqManual} d`
      : (mapejos.freqTasca[t.Freq] || "");
    return (
      <>
        {" "}
        <span className="dv2-chip">Cíclica{freq ? ` · ${freq}` : ""}</span>
      </>
    );
  };

  const obrirExecucioCicle = (idTasques) => {
    const trobada = tasquesList.find((t) => Number(t.idTasques) === Number(idTasques));
    if (!trobada) return;
    setSelectedTasca(trobada);
    setActiveTab("consultar");
  };

  const renderArbre = (idPare, level) => {
    const filtered = filtrarTasques();
    const fills = filtered
      .filter((t) => idTascaVal(t.idTascaPare) === idTascaVal(idPare))
      .sort(compararPrevista);
    if (fills.length === 0) return null;

    return fills.map((t) => {
      const isExp = expandedIds.includes(t.idTasques);
      const isSel = selectedTasca?.idTasques === t.idTasques;
      const hasChildren = teFills(t.idTasques);
      const nest = classeNiuFila(level);

      return (
        <React.Fragment key={t.idTasques}>
          <tr
            ref={isSel ? selectedRowRef : null}
            className={`${isSel ? "is-selected" : ""} ${isExp ? "is-open" : ""} ${nest}`.trim()}
            onClick={() => handleRowClick(t)}
            onDoubleClick={() => potConsultar && obrirConsultar(t)}
          >
            <td
              className="dv2-col-expand"
              style={{ paddingLeft: 8 + level * 16 }}
              onClick={(e) => toggleExpand(e, t)}
            >
              <button
                type="button"
                className={`dv2-expandBtn ${isExp ? "is-open" : ""} ${hasChildren ? "has-children" : ""}`}
                aria-label={isExp ? "Plegar" : "Expandir per crear subtasques"}
                onClick={(e) => toggleExpand(e, t)}
              >
                {isExp ? "▼" : "▶"}
              </button>
            </td>
            {renderColumnesRow(t)}
            <td className="dv2-table-actions">{renderAccions(t)}</td>
          </tr>
          {isExp && renderArbre(t.idTasques, level + 1)}
        </React.Fragment>
      );
    });
  };

  const renderArbreMobil = (idPare, level) => {
    const filtered = filtrarTasques();
    const fills = filtered
      .filter((t) => idTascaVal(t.idTascaPare) === idTascaVal(idPare))
      .sort(compararPrevista);
    if (fills.length === 0) return null;
    return fills.map((t) => {
      const isExp = expandedIds.includes(t.idTasques);
      const isSel = selectedTasca?.idTasques === t.idTasques;
      const hasChildren = teFills(t.idTasques);
      const nest = classeNiuFila(level);
      return (
        <React.Fragment key={t.idTasques}>
          <article
            className={`dv2-record ${isSel ? "is-selected" : ""} ${nest}`.trim()}
            style={{ marginLeft: level * 12 }}
            onClick={() => handleRowClick(t)}
            onDoubleClick={() => potConsultar && obrirConsultar(t)}
          >
            <div>
              <p className="dv2-record-title">{t.DescripcioTasca || "—"}{etiquetaCicle(t)}</p>
              <p className="dv2-record-meta">{metaMobil(t) || "—"}</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button
                type="button"
                className={`dv2-expandBtn ${isExp ? "is-open" : ""} ${hasChildren ? "has-children" : ""}`}
                aria-label={isExp ? "Plegar" : "Expandir per crear subtasques"}
                onClick={(e) => toggleExpand(e, t)}
              >
                {isExp ? "▼" : "▶"}
              </button>
              {renderAccions(t)}
            </div>
          </article>
          {isExp && renderArbreMobil(t.idTasques, level + 1)}
        </React.Fragment>
      );
    });
  };

  const reFetchTasques = async () => {
    const prevId = selectedTasca?.idTasques;
    const list = await fetchTasques();
    setActiveTab("table");
    if (prevId && list.length > 0) {
      const updated = list.find((t) => t.idTasques === prevId);
      setSelectedTasca(updated || null);
    }
  };

  const tascaMenu = menuObert ? tasquesList.find((t) => t.idTasques === menuObert) : null;

  const renderFitxa = () => {
    switch (activeTab) {
      case "nou":
        return (
          <DetallsTasca
            mode="nou"
            tasca={nouInfo.parentId ? tasquesList.find((t) => t.idTasques === nouInfo.parentId) || null : null}
            treball={treball}
            isSubtasca={nouInfo.isSubtasca}
            nivellText={nouInfo.nivellText}
            onSave={reFetchTasques}
            onCancel={() => setActiveTab("table")}
          />
        );
      case "consultar":
        return selectedTasca ? (
          <>
            {tascaAmbLogistica(selectedTasca) && (
              <ResumLogisticaTasca
                tasca={selectedTasca}
                idTreball={treball?.idTreballs}
                onAprovisionar={() => obrirLogistica(selectedTasca, "consultar")}
                onVeureTreball={onVeureLogistica}
              />
            )}
            <DetallsTasca
              key={selectedTasca.idTasques}
              mode="consultar"
              tasca={selectedTasca}
              treball={treball}
              tasquesList={tasquesList}
              onNavigate={setSelectedTasca}
              onObrirCicle={obrirExecucioCicle}
              onCancel={() => setActiveTab("table")}
            />
          </>
        ) : null;
      case "modificar":
        return selectedTasca ? (
          <DetallsTasca
            mode="modificar"
            tasca={selectedTasca}
            treball={treball}
            onSave={reFetchTasques}
            onCancel={() => setActiveTab("table")}
          />
        ) : null;
      case "esborrar":
        return selectedTasca ? (
          <DetallsTasca
            mode="esborrar"
            tasca={selectedTasca}
            treball={treball}
            onSave={() => {
              setSelectedTasca(null);
              reFetchTasques();
            }}
            onCancel={() => setActiveTab("table")}
          />
        ) : null;
      case "logistica":
        return selectedTasca ? <LogisticaTasca key={selectedTasca.idTasques} tasca={selectedTasca} /> : null;
      default:
        return null;
    }
  };

  const tabInfo = getNewTabInfo();
  const titolTasques =
    activeTab === "nou"
      ? nouInfo.label
      : activeTab === "consultar"
      ? "Tasca"
      : activeTab === "modificar"
      ? "Modificar tasca"
      : activeTab === "esborrar"
      ? "Esborrar tasca"
      : activeTab === "logistica"
      ? "Logística de la tasca"
      : "Tasques";

  return (
    <div className="dv2-entity">
      {mostraAjuda && (
        <AjudaTreball
          pestanya={
            activeTab === "logistica" ? "logistica" : activeTab === "table" ? "tasques" : "fitxaTasca"
          }
          onTancar={() => setMostraAjuda(false)}
        />
      )}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${activeTab === "esborrar" ? "is-danger" : ""}`} aria-hidden="true">
            {activeTab === "esborrar" ? "🗑️" : "📋"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titolTasques}</h1>
            <p className="dv2-pageHeader-subtitle">
              {treball?.idTreballIntern || treball?.idTreballs}
              {treball?.NomClient ? ` · ${treball.NomClient}` : ""}
              {activeTab === "logistica" && selectedTasca?.DescripcioTasca
                ? ` · ${selectedTasca.DescripcioTasca}`
                : ""}
            </p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <button
            type="button"
            className="dv2-btn dv2-btn--icon dv2-btn--ghost"
            title={activeTab === "logistica" ? "Com funciona la logística" : "Com funcionen les Tasques"}
            aria-label="Ajuda"
            onClick={() => setMostraAjuda(true)}
          >
            ?
          </button>
          <button
            type="button"
            className="dv2-btn"
            onClick={
              activeTab === "table"
                ? onCancel
                : () => setActiveTab(activeTab === "logistica" ? origenLogistica : "table")
            }
          >
            Tornar
          </button>
          {activeTab === "table" && potCrear && (
            <button
              type="button"
              className="dv2-btn dv2-btn--primary"
              title={tabInfo.nivellText}
              onClick={obrirNou}
            >
              {tabInfo.label}
            </button>
          )}
          {activeTab === "consultar" && potModificar && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => setActiveTab("modificar")}>
              Modificar
            </button>
          )}
          {activeTab === "consultar" && selectedTasca && tascaAmbLogistica(selectedTasca) && (
            <button type="button" className="dv2-btn" onClick={() => obrirLogistica(selectedTasca, "consultar")}>
              Logística
            </button>
          )}
        </div>
      </div>
      {subnav}

      {activeTab === "table" ? (
        <div className="dv2-entity-body">
          <div className="dv2-filterBar">
            <label className="dv2-filter">
              <span className="dv2-filter-label">Vista</span>
              <select className="dv2-select" value={vista} onChange={(e) => setVista(e.target.value)}>
                {VISTES.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="dv2-filter dv2-filter--grow">
              <span className="dv2-filter-label">Descripció</span>
              <input
                type="text"
                className="dv2-input"
                value={fDescripcio}
                onChange={(e) => setFDescripcio(e.target.value)}
                placeholder="Cercar…"
              />
            </div>
            {isNarrow && (
              <button
                type="button"
                className={`dv2-btn ${filtresOberts ? "dv2-btn--primary" : ""}`}
                onClick={() => setFiltresOberts((v) => !v)}
              >
                {filtresOberts ? "Amagar filtres" : "Més filtres"}
              </button>
            )}
            {(!isNarrow || filtresOberts) && (
              <>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Estat</span>
              <select className="dv2-select" value={fEstat} onChange={(e) => setFEstat(e.target.value)}>
                <option value="">Tots</option>
                <option value="__obertes">No acabades</option>
                {Object.entries(mapejos.estatTasca).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Prioritat</span>
              <select className="dv2-select" value={fPrioritat} onChange={(e) => setFPrioritat(e.target.value)}>
                <option value="">Totes</option>
                {Object.entries(mapejos.prioritatTasca).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Categoria</span>
              <select className="dv2-select" value={fCategoria} onChange={(e) => setFCategoria(e.target.value)}>
                <option value="">Totes</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Agrupació</span>
              <select className="dv2-select" value={fAgrupacio} onChange={(e) => setFAgrupacio(e.target.value)}>
                <option value="">Totes</option>
                {agrupacions.map(([id, nom]) => (
                  <option key={id} value={id}>
                    {nom}
                  </option>
                ))}
              </select>
            </label>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Grup MO</span>
              <select className="dv2-select" value={fGrupMO} onChange={(e) => setFGrupMO(e.target.value)}>
                <option value="">Tots</option>
                {grupsUnics.map(([id, nom]) => (
                  <option key={id} value={id}>
                    {nom}
                  </option>
                ))}
              </select>
            </label>
              </>
            )}
          </div>

          {tabInfo.nivellText ? <p className="dv2-hint">{tabInfo.nivellText}</p> : null}

          {tasquesList.length === 0 ? (
            <p className="dv2-empty">No hi ha tasques per aquest treball.</p>
          ) : filtrarTasques().length === 0 ? (
            <p className="dv2-empty">Cap tasca no coincideix amb els filtres.</p>
          ) : isNarrow ? (
            <div className="dv2-recordList" style={{ display: "flex" }}>
              {renderArbreMobil(null, 0)}
            </div>
          ) : (
            <div className="dv2-tableWrap">
              <table className="dv2-table">
                <thead>{renderColumnesHeader()}</thead>
                <tbody>{renderArbre(null, 0)}</tbody>
              </table>
            </div>
          )}

          {tascaMenu &&
            menuPos &&
            createPortal(
              <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
                {potConsultar && (
                  <button type="button" className="dv2-menu-item" onClick={() => obrirConsultar(tascaMenu)}>
                    Consultar
                  </button>
                )}
                {potModificar && (
                  <button
                    type="button"
                    className="dv2-menu-item"
                    onClick={() => {
                      setSelectedTasca(tascaMenu);
                      setMenuObert(null);
                      setActiveTab("modificar");
                    }}
                  >
                    Modificar
                  </button>
                )}
                {potConsultar && tascaAmbLogistica(tascaMenu) && (
                  <button type="button" className="dv2-menu-item" onClick={() => obrirLogistica(tascaMenu, "table")}>
                    Logística
                  </button>
                )}
                {potCrear && (
                  <button type="button" className="dv2-menu-item" onClick={() => obrirNovaSubtasca(tascaMenu)}>
                    Nova subtasca
                  </button>
                )}
                {potEsborrar && (
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => {
                      setSelectedTasca(tascaMenu);
                      setMenuObert(null);
                      setActiveTab("esborrar");
                    }}
                  >
                    Esborrar
                  </button>
                )}
              </div>,
              document.body
            )}
        </div>
      ) : (
        renderFitxa()
      )}
    </div>
  );
}

export default Tasques;
